// mission-initializer-pin-baseline.ts
//
// R-24: without a VERIFIED tuple captured at init, `detectPinDrift` reads "no
// baseline yet" as "not drift" (pack-pin-drift-detector.ts's own contract) —
// so §18's pin-drift guard silently never fires for a mission until an
// operator happens to run `doctor --reverify`. This module writes that first
// baseline right after init finishes building the mission. The probe MUST be
// the same `claude --version` invocation used at dispatch time
// (`probeClaudeVersion` in autopilot-ops-checks-cli-wiring.ts) — a different
// probe/format here would make every mission look drifted from tick one.

import { probeClaudeVersion } from "./autopilot-ops-checks-cli-wiring.js";
import {
  captureVerifiedTuple,
  type PinTuple,
  readVerifiedTuple,
  writeVerifiedTuple,
} from "./pack-pin-drift-detector.js";

export interface WriteInitialPinBaselineInput {
  missionWorkspace: string;
  controlRoot: string;
  now: () => Date;
  /** Injectable — tests never spawn `claude` for real. Defaults to the shared dispatch-time probe. */
  probeClaudeVersion?: () => Promise<string>;
  // (code-review phase-02 [H1]) compatibility.json and the pin baseline are
  // written by two non-transactional steps in mission-initializer.ts — a
  // crash landing between them left compatibility.json present but no
  // baseline, and the idempotent short-circuit used to skip this write
  // FOREVER on every re-run, silently disarming §18's pin-drift guard. Set
  // by the idempotent branch so a re-run only backfills a still-missing
  // baseline instead of unconditionally re-probing on an already-healthy one.
  onlyIfMissing?: boolean;
}

/** Captures the current tuple and persists it as the mission's first VERIFIED baseline. */
export async function writeInitialPinBaseline(
  input: WriteInitialPinBaselineInput,
): Promise<PinTuple | undefined> {
  if (input.onlyIfMissing && (await readVerifiedTuple(input.controlRoot)) !== undefined) {
    return undefined;
  }
  const tuple = await captureVerifiedTuple({
    missionWorkspace: input.missionWorkspace,
    controlRoot: input.controlRoot,
    now: input.now,
    probeClaudeVersion: input.probeClaudeVersion ?? probeClaudeVersion,
  });
  await writeVerifiedTuple(input.controlRoot, tuple);
  return tuple;
}
